import React, { useEffect, useState, useRef } from 'react';
import { useLazyQuery } from 'react-apollo';
import { injectIntl } from 'react-intl';
import MediaQuery from 'react-responsive';
import moment from 'moment';
import Pagination from '@uniiverse/library/components/pagination';
import { Dropdown } from 'semantic-ui-react';
import { Input } from '../../inputs';
import FilterSign from '../../../assets/add-ons/filter.svg';
import AddOnsIcon from '../../../assets/add-ons/addOns.svg';
import Close from '../../../assets/add-ons/close_blue.svg';
import Search from '../../my-events/search';
import LoadingSpinner from '../../loading';
import query from './get-add-on-cost-items.gql';
import styles from './index-module.css';
import { voidCostItems } from '../../../lib/api-helper';
import AddOnOrderRow from './row';

import messages from './locale';

import {
  ALL_DAYS,
  ALL_STATUSES,
  SMALL_DEVICE_BREAK_POINT,
  ADD_ON_ORDERS_LIMIT,
  PAST_7_DAYS,
  PAST_30_DAYS,
  REDEEMED,
  NOT_REDEEMED,
  ADD_ON_LISTING_STATES,
  HOSTED_LISTING_LIMIT,
} from './consts';
import { MANUALLY_CHECKED_IN, CHECKED_OUT } from './check-in-state';

const Table = injectIntl((props) => {
  const [addOnOrderItems, setAddOnOrderItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchText, setSearchText] = useState(null);
  const [searchTracked, setSearchTracked] = useState(false);
  const [timeFilter, setTimeFilter] = useState(ALL_DAYS);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [timeFilterTracked, setTimeFilteredTracked] = useState(false);
  const [statusFilterTracked, setStatusFilterTracked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [isEventSelectorFocused, setIsEventSelectorFocused] = useState(false);
  const isEventSelected = useRef(false);
  const [searchEventKey, setSearchEventKey] = useState('');
  const [allListings, setAllListings] = useState(null);
  const isHostChanged = useRef(false);
  const isFirstFetch = useRef(true);
  const [selectedListing, setSelectedListing] = useState(null);

  const [fetchAddons, { loading: addOnsLoading }] = useLazyQuery(query, {
    variables: {
      hostId: props.hostId,
      search: null,
      from: null,
      checkInState: null,
      eventStates: ADD_ON_LISTING_STATES,
      eventLimit: HOSTED_LISTING_LIMIT,
      limit: 15,
      offset: 0,
    },
    skip: !isFirstFetch,
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      const currentPageCount = Math.ceil(response?.host?.addOnOrderItems?.totalCount / ADD_ON_ORDERS_LIMIT);
      setAddOnOrderItems(response?.host?.addOnOrderItems?.nodes);
      setPageCount(currentPageCount);
      setAllListings(response.host?.events?.nodes);
      if ((allListings === null || allListings.length === 0 || isHostChanged.current)) {
        isHostChanged.current = false;
      }
    },
  });

  useEffect(() => {
    handleChangePage({ selected: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, timeFilter, statusFilter]);

  useEffect(() => {
    isHostChanged.current = true;
    isFirstFetch.current = true;
    fetchAddons();
  }, [props.hostId, fetchAddons]);

  const getFromDate = () => {
    switch (timeFilter) {
      case ALL_DAYS:
        return null;
      case PAST_7_DAYS:
        return moment()
          .subtract(7, 'd')
          .format('YYYY-MM-DD');
      case PAST_30_DAYS:
        return moment()
          .subtract(30, 'd')
          .format('YYYY-MM-DD');
      default:
        return null;
    }
  }

  const handleOrderItemStatusChange = () => handleChangePage({ selected: currentPage - 1 });

  const onRefund = (ticketIds, costItemIds) => {
    voidCostItems(ticketIds, costItemIds).then(
      handleChangePage({ selected: 0 })
    );
  }

  const renderPagination = () => {
    if (pageCount > 1) {
      return (
        <div className={styles.paginationContainer}>
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            onPageChange={handleChangePage}
          />
        </div>
      );
    }
    return <></>;
  }

  const handleChangePage = ({ selected }) => {
    fetchAddons({
      variables: {
        hostId: props.hostId,
        search: searchText,
        from: getFromDate(),
        checkInState: getCheckInFilterArgument(),
        limit: 15,
        offset: selected * 15,
        listingId: selectedListing,
      }
    });

    setCurrentPage(selected + 1);
    setSearchLoading(false);
    window.scrollTo(0, 0);
  }

  const renderTableHeader = () => {
    return (
      addOnOrderItems.length > 0 &&
      <div className={styles.addOnOrdersHeader}>
        <div className={styles.buyerDetails}>
          {props.intl.formatMessage(messages.attendee)}
        </div>
        <div className={styles.addOnName}>
          {props.intl.formatMessage(messages.addOn)}
        </div>
        <div className={styles.eventName}>
          {props.intl.formatMessage(messages.eventName)}
        </div>
        <div className={styles.orderDate}>
          {props.intl.formatMessage(messages.date)}
        </div>
        <div className={styles.checkInState}>
          {props.intl.formatMessage(messages.checkInState)}
        </div>
        <div className={styles.actions} />
      </div>
    );
  }

   // This function maps the statusFilter front-end names to the arguments for the back-end
  const getCheckInFilterArgument = () => {
    switch (statusFilter) {
      case ALL_STATUSES:
        return null;
      case REDEEMED:
        return MANUALLY_CHECKED_IN;
      case NOT_REDEEMED:
        return CHECKED_OUT;
      default:
        return null;
    }
  }

  const onUpdateSearchText = (text) => {
    if (!searchTracked) {
      props.trackAmplitude('Add-ons: Orders Search');
      setSearchTracked(true);
    }
    setSearchText(text);
  }

  const renderSearch = () => {
    return (
      <Search
        intl={props.intl}
        placeholder={props.intl.formatMessage(messages.searchAddon)}
        onShowLoadingState={() => setSearchLoading(true)}
        onUpdateSearchText={onUpdateSearchText}
        customWrapperClass={styles.searchInputWrapper}
        searchValue={searchText || ''}
      />
    );
  }

  const renderTableBody = () => {
    if (addOnsLoading || searchLoading) {
      return <LoadingSpinner />;
    }

    if (addOnOrderItems.length <= 0) {
      return (
        <div className={styles.noAddOnsContainer}>
          <div><AddOnsIcon /></div>
          <div className={styles.noAddOnsMessage}>
            {props.intl.formatMessage(messages.noEventsFoundOnFilters)}
          </div>
        </div>
      );
    }

    return (
      <div>
        {addOnOrderItems.map((addOnOrderItem, orderIndex) => (
          <AddOnOrderRow
            addOnOrderItem={addOnOrderItem}
            intl={props.intl}
            handleOrderItemStatusChange={handleOrderItemStatusChange}
            onRefund={onRefund}
            // eslint-disable-next-line react/no-array-index-key
            key={orderIndex}
            trackAmplitude={props.trackAmplitude}
          />
        ))}
        {renderPagination()}
      </div>
    );
  }

  const handleStatusFilterChange = (status) => {
    if (!statusFilterTracked) {
      props.trackAmplitude('Add-ons: Orders Filtered Status');
      setStatusFilterTracked(true);
    }
    setStatusFilter(status);
  }

  const handleTimeFilterChange = (timeFilterValue) => {
    if (!timeFilterTracked) {
      props.trackAmplitude('Add-ons: Orders Filtered Time');
      setTimeFilteredTracked(true);
    }
    setTimeFilter(timeFilterValue);
  }

  const renderStatusButtons = () => {
    return  <div>
      <Dropdown
        className={styles.selector}
        fluid
        selection
        options={[
          {text: props.intl.formatMessage(messages.allStatuses), value: ALL_STATUSES},
          {text: props.intl.formatMessage(messages.redeemed), value: REDEEMED},
          {text: props.intl.formatMessage(messages.notRedeemed), value: NOT_REDEEMED}
        ]}
        value={statusFilter}
        onChange={(event, { value }) => {
          handleStatusFilterChange(value)
        }}
        onBlur={(e) => {e.preventDefault()}}
        selectOnBlur={false}
      />
    </div>
  }

  const handleEventSearch = (eventSearch) => {
    const filteredText = eventSearch?.length > 0 ? eventSearch : '';
    isEventSelected.current = false;
    setSearchEventKey(filteredText);
  }

  const handleSpaceDownOnEventInput = (event) => {
    const SPACE_BAR_CODE = 32;
    if (event.keyCode === SPACE_BAR_CODE) {
      event.target.value += ' '; 
      event.stopPropagation();
    }
  }

  const getFilteredList = () => {
    const searchString = `${searchEventKey}`;
    const regexp = new RegExp(searchString, 'i');
    return (allListings && regexp) ? [...allListings]?.filter(listing => regexp.test(listing.title)) : [];
  }

  const renderDropdownItemText = (text, searchKeyLength) => {
    if (searchKeyLength) {
      const regex = new RegExp(searchEventKey, 'gi');
      const parts = text.split(regex);
      const matches = text.match(regex);
      return (
        <div>
          {parts.map((part, index) => (
            <React.Fragment>
              {part}
              {matches && index < matches.length && (
                <strong>{matches[index]}</strong>
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }
    return <div>{text}</div>;
  }

  const setSelectedListingArgument = (listing) => {
    setSearchEventKey(listing ? listing.title : '');
  }

  const handleSuggestedEventSearch = (listingId, currency, title) => {
    setSelectedListing(listingId);
    setSelectedListingArgument({listingId, currency, title});
    setIsEventSelectorFocused(false);
    setCurrentPage(1);
    isEventSelected.current = true;
    isFirstFetch.current = false;
    fetchAddons({
      variables: {
        hostId: props.hostId,
        search: searchText,
        from: getFromDate(),
        checkInState: getCheckInFilterArgument(),
        listingId,
        currency,
        eventStates: ADD_ON_LISTING_STATES,
        eventLimit: HOSTED_LISTING_LIMIT,
      }
    });
  }

  const removeEventFilter = () => {
    setSelectedListingArgument();
    setSelectedListing();
    setCurrentPage(1);
    isEventSelected.current = false;
    isFirstFetch.current = false;
    fetchAddons({
      variables: {
        hostId: props.hostId,
        limit: 15,
        offset: 0,
        search: searchText,
        from: getFromDate(),
        checkInState: getCheckInFilterArgument(),
        eventStates: ADD_ON_LISTING_STATES,
        eventLimit: HOSTED_LISTING_LIMIT,
      }
    });
  }

  const renderCancelIcon = <span
    className={styles.removeFilterIcon}
    onClick={removeEventFilter}
    role="button"
    tabIndex={0}
    >
      <Close color="black" />
    </span>;

  const renderAddonsFilterByEvent = () => {
    const filterEventClass = 
      `${styles.addonsFilterByEvent} ${isEventSelected.current ? styles.addonsFilterByEventSelected : ''}`;
    return <div className={filterEventClass}>
      {isEventSelected.current ? renderCancelIcon : ''}
      <Dropdown
        trigger={
          <Input
            value={searchEventKey}
            onChange={(e) => {
              handleEventSearch(e.target.value);
            }}
            placeholder={props.intl.formatMessage(messages.eventSearchPlaceholder)}
            icon={() => <FilterSign className={styles.filterIcon} color="black" />}
            onKeyDown={handleSpaceDownOnEventInput}
          />
        }
        searchQuery={searchEventKey}
        onFocus={_ => setIsEventSelectorFocused(true)}
        onBlur={_ => setIsEventSelectorFocused(false)}
      >
        <Dropdown.Menu open={isEventSelectorFocused}>
          {getFilteredList()?.map(({ id, title, currency }) => {
            return <Dropdown.Item
              key={id}
              role="button"
              tabIndex={0}
              text={() => renderDropdownItemText(title, searchEventKey?.length)}
              onClick={() => handleSuggestedEventSearch(id, currency, title)}
            />
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  }

  const renderTimeButtons = () => {
    return  <div>
      <Dropdown
        className={styles.selector}
        fluid
        selection
        options={[
          {text: props.intl.formatMessage(messages.allDays), value: ALL_DAYS},
          {text: props.intl.formatMessage(messages.pastSevenDays), value: PAST_7_DAYS},
          {text: props.intl.formatMessage(messages.pastThirtyDays), value: PAST_30_DAYS}
        ]}
        value={timeFilter}
        onChange={(event, { value }) => {
          handleTimeFilterChange(value)
        }}
        onBlur={(e) => {e.preventDefault()}}
        selectOnBlur={false}
      />
    </div>
  }

  return (
    <div className={styles.addOnOrdersTableContainer}>
      <MediaQuery minWidth={SMALL_DEVICE_BREAK_POINT + 1}>
        <div className={styles.filterContainer}>
          <div>{renderSearch()}</div>
          <div>{renderAddonsFilterByEvent()}</div>
          {renderStatusButtons()}
          {renderTimeButtons()}
        </div>
        {renderTableHeader()}
      </MediaQuery>
      <MediaQuery maxWidth={SMALL_DEVICE_BREAK_POINT}>
        <div className={styles.filters}>
          <div className={styles.filtersLeft}>
            {renderSearch()}
            <div>{renderAddonsFilterByEvent()}</div>
            {renderStatusButtons()}
            {renderTimeButtons()}
          </div>
        </div>
      </MediaQuery>
      {renderTableBody()}
    </div>
  );
})

export default Table;
